import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { featuredMembers } from '@/mocks/members';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import SEOHead from '@/components/feature/SEOHead';

export default function ProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const member = featuredMembers.find(m => m.id === Number(id));
  const [activePhoto, setActivePhoto] = useState(0);
  const [liked, setLiked] = useState(false);
  const [videoModal, setVideoModal] = useState(false);
  const [messageModal, setMessageModal] = useState(false);
  const [message, setMessage] = useState('');
  const [messageSent, setMessageSent] = useState(false);

  if (!member) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">未找到该会员</p>
          <button onClick={() => navigate('/')} className="text-red-600 cursor-pointer">返回首页</button>
        </div>
      </div>
    );
  }

  const allPhotos = [member.avatar, ...member.photos];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={`${member.name} - 会员资料 | 富缘佳旺 FortuneMatch`}
        description={`查看${member.name}的详细资料，${member.age}岁，${member.location}，${member.education}。`}
        noIndex={true}
      />
      <Navbar />

      {/* Back Button */}
      <div className="pt-20 pb-0">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-800 transition-colors cursor-pointer text-sm"
          >
            <i className="ri-arrow-left-line"></i> 返回
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Left Column - Photos & Quick Info */}
          <div className="lg:col-span-1">
            {/* Main Photo */}
            <div className="bg-white rounded-2xl overflow-hidden mb-4">
              <div className="relative">
                <div className="w-full h-80 overflow-hidden">
                  <img
                    src={allPhotos[activePhoto]}
                    alt={member.name}
                    className="w-full h-full object-cover object-top transition-all duration-500"
                  />
                </div>
                {/* Badges overlay */}
                <div className="absolute top-3 left-3 flex flex-col gap-2">
                  {member.videoVerified && (
                    <div className="flex items-center gap-1 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                      <i className="ri-video-line text-xs"></i> 视频认证
                    </div>
                  )}
                  {member.idVerified && (
                    <div className="flex items-center gap-1 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                      <i className="ri-id-card-line text-xs"></i> 实名认证
                    </div>
                  )}
                </div>
                <div className="absolute top-3 right-3 bg-yellow-400 text-yellow-900 text-xs px-2 py-1 rounded-full font-semibold">
                  {member.education.split('·')[0].trim()}
                </div>
                {/* Match */}
                <div className="absolute bottom-3 right-3 w-14 h-14 flex items-center justify-center bg-white rounded-full">
                  <div className="text-center">
                    <div className="text-red-600 font-bold text-base leading-none">{member.match}%</div>
                    <div className="text-gray-400 text-[9px]">匹配度</div>
                  </div>
                </div>
              </div>

              {/* Thumbnail strip */}
              <div className="p-3 flex gap-2">
                {allPhotos.map((photo, i) => (
                  <button
                    key={i}
                    onClick={() => setActivePhoto(i)}
                    className={`w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all cursor-pointer ${activePhoto === i ? 'border-red-500' : 'border-transparent'}`}
                  >
                    <img src={photo} alt="" className="w-full h-full object-cover object-top" />
                  </button>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 mb-4">
              <button
                onClick={() => setVideoModal(true)}
                className="w-full flex items-center justify-center gap-2 bg-red-600 text-white py-3 rounded-full font-semibold hover:bg-red-700 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-video-chat-line text-lg"></i> 发起视频通话
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setMessageModal(true)}
                  className="flex-1 flex items-center justify-center gap-2 border-2 border-red-600 text-red-600 py-2.5 rounded-full font-semibold hover:bg-red-50 transition-all cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-message-3-line"></i> 发消息
                </button>
                <button
                  onClick={() => setLiked(!liked)}
                  className={`w-12 h-11 flex items-center justify-center rounded-full border-2 transition-all cursor-pointer ${liked ? 'bg-red-50 border-red-400 text-red-500' : 'border-gray-200 text-gray-400 hover:border-red-300'}`}
                >
                  <i className={liked ? 'ri-heart-fill text-lg' : 'ri-heart-line text-lg'}></i>
                </button>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-2xl p-5">
              <h4 className="text-sm font-bold text-gray-800 mb-4">基本信息</h4>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: 'ri-ruler-line', label: '身高', value: member.height },
                  { icon: 'ri-scales-line', label: '体重', value: member.weight },
                  { icon: 'ri-map-pin-line', label: '所在地', value: member.location },
                  { icon: 'ri-moon-line', label: '星座', value: member.zodiac },
                  { icon: 'ri-briefcase-line', label: '职业', value: member.job },
                  { icon: 'ri-money-cny-circle-line', label: '收入', value: member.income },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-7 h-7 flex items-center justify-center bg-red-50 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-red-400 text-sm`}></i>
                    </div>
                    <div>
                      <div className="text-[10px] text-gray-400">{item.label}</div>
                      <div className="text-xs font-semibold text-gray-700">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Full Profile */}
          <div className="lg:col-span-2 flex flex-col gap-6">

            {/* Name & Header */}
            <div className="bg-white rounded-2xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                    {member.name}
                  </h1>
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    <span className="text-gray-500 text-sm">{member.age}岁</span>
                    <span className="text-gray-300">·</span>
                    <span className="text-gray-500 text-sm flex items-center gap-1">
                      <i className="ri-map-pin-line text-red-400"></i>{member.location}
                    </span>
                    <span className="text-gray-300">·</span>
                    <span className="text-gray-500 text-sm flex items-center gap-1">
                      <i className="ri-graduation-cap-line text-red-400"></i>{member.education}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap justify-end">
                  {member.videoVerified && (
                    <span className="flex items-center gap-1 bg-green-50 text-green-600 text-xs px-3 py-1 rounded-full border border-green-200">
                      <i className="ri-video-line"></i> 视频认证
                    </span>
                  )}
                  {member.eduVerified && (
                    <span className="flex items-center gap-1 bg-yellow-50 text-yellow-700 text-xs px-3 py-1 rounded-full border border-yellow-200">
                      <i className="ri-graduation-cap-line"></i> 学历认证
                    </span>
                  )}
                </div>
              </div>

              {/* Bio */}
              <div className="border-t border-gray-50 pt-4">
                <h4 className="text-sm font-bold text-gray-700 mb-2">个人简介</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{member.bio}</p>
              </div>

              {/* Looking For */}
              <div className="mt-4 bg-rose-50 rounded-xl p-4">
                <div className="flex items-start gap-2">
                  <i className="ri-heart-line text-red-400 mt-0.5"></i>
                  <div>
                    <div className="text-xs font-bold text-red-600 mb-1">寻找对象要求</div>
                    <p className="text-sm text-gray-600">{member.lookingFor}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Education & Credentials */}
            <div className="bg-white rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 flex items-center justify-center bg-yellow-50 rounded-lg">
                  <i className="ri-graduation-cap-line text-yellow-500"></i>
                </div>
                <h4 className="text-base font-bold text-gray-800">教育背景</h4>
                {member.eduVerified && (
                  <span className="ml-auto flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full border border-green-100">
                    <i className="ri-shield-check-fill"></i> 学信网已核实
                  </span>
                )}
              </div>

              <div className="mb-5">
                <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl border border-yellow-100">
                  <div className="w-12 h-12 flex items-center justify-center bg-yellow-100 rounded-xl flex-shrink-0">
                    <i className="ri-building-line text-yellow-600 text-xl"></i>
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-gray-800">{member.school}</div>
                    <div className="text-sm text-gray-500">{member.education} · {member.major}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{member.gradYear}年毕业</div>
                    <div className="flex items-center gap-1 mt-1.5">
                      <i className="ri-shield-check-fill text-green-500 text-xs"></i>
                      <span className="text-xs text-green-600 font-medium">学历证书已核实认证</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                {member.credentials.map((cred, i) => (
                  <div key={i} className="flex items-center justify-between p-3 border border-gray-100 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center bg-gray-50 rounded-lg flex-shrink-0">
                        <i className="ri-award-line text-gray-500"></i>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-700">{cred.type}</div>
                        <div className="text-xs text-gray-400">{cred.institution} · {cred.year}年</div>
                      </div>
                    </div>
                    {cred.verified && (
                      <div className="flex items-center gap-1 text-green-600 text-xs font-medium whitespace-nowrap">
                        <i className="ri-shield-check-fill"></i> 已验证
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Work Experience */}
            <div className="bg-white rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 flex items-center justify-center bg-red-50 rounded-lg">
                  <i className="ri-briefcase-line text-red-500"></i>
                </div>
                <h4 className="text-base font-bold text-gray-800">工作经历</h4>
                {member.workVerified && (
                  <span className="ml-auto flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full border border-green-100">
                    <i className="ri-shield-check-fill"></i> 企业公示已核实
                  </span>
                )}
              </div>

              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-gray-100"></div>
                <div className="flex flex-col gap-6">
                  {member.workExperience.map((work, i) => (
                    <div key={i} className="flex gap-4 relative">
                      {/* Timeline dot */}
                      <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 z-10 ${work.current ? 'bg-red-600' : 'bg-gray-200'}`}>
                        <i className={`ri-briefcase-line text-xs ${work.current ? 'text-white' : 'text-gray-500'}`}></i>
                      </div>
                      <div className="flex-1 pb-2">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div>
                            <div className="font-bold text-gray-800 text-sm">{work.title}</div>
                            <div className="text-sm text-gray-600">{work.company}</div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className={`text-xs font-medium px-2 py-0.5 rounded-full ${work.current ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-500'}`}>
                              {work.current ? '在职' : '已离职'}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">{work.startYear} - {work.current ? '至今' : work.endYear}</div>
                          </div>
                        </div>
                        {work.description && (
                          <p className="text-xs text-gray-500 leading-relaxed bg-gray-50 rounded-lg p-3 mt-2">{work.description}</p>
                        )}
                        {work.verified && (
                          <div className="flex items-center gap-1 mt-2">
                            <i className="ri-shield-check-fill text-green-500 text-xs"></i>
                            <span className="text-xs text-green-600">工作经历已通过企业公示系统核实</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Interests & Languages */}
            <div className="bg-white rounded-2xl p-6">
              <h4 className="text-base font-bold text-gray-800 mb-4">兴趣爱好</h4>
              <div className="flex flex-wrap gap-2 mb-5">
                {member.interests.map((interest, i) => (
                  <span key={i} className="px-3 py-1.5 bg-rose-50 text-rose-600 rounded-full text-sm border border-rose-100">
                    {interest}
                  </span>
                ))}
              </div>

              <h4 className="text-base font-bold text-gray-800 mb-3">语言能力</h4>
              <div className="flex flex-wrap gap-2">
                {member.languages.map((lang, i) => (
                  <span key={i} className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm border border-blue-100">
                    <i className="ri-translate-2 mr-1"></i>{lang}
                  </span>
                ))}
              </div>
            </div>

            {/* Photo Gallery */}
            <div className="bg-white rounded-2xl p-6">
              <h4 className="text-base font-bold text-gray-800 mb-4">
                <i className="ri-image-line text-red-400 mr-2"></i>生活照片
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {member.photos.map((photo, i) => (
                  <div
                    key={i}
                    onClick={() => setActivePhoto(i + 1)}
                    className="aspect-video rounded-xl overflow-hidden cursor-pointer group"
                  >
                    <img
                      src={photo}
                      alt={`${member.name} photo ${i + 1}`}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Safety Note */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-5 border border-green-100">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-xl flex-shrink-0">
                  <i className="ri-shield-check-line text-green-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-green-800 mb-1">安全保障说明</h4>
                  <p className="text-xs text-green-700 leading-relaxed">
                    该会员已完成身份证实名认证、学历证书核实及视频身份验证。富缘佳旺承诺所有展示信息均经过严格审核，确保您的交友安全。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* Video Chat Modal */}
      {videoModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setVideoModal(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center" onClick={e => e.stopPropagation()}>
            <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4">
              <i className="ri-video-chat-line text-red-500 text-2xl"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">与 {member.name} 视频通话</h3>
            <p className="text-gray-500 text-sm mb-6">视频通话是我们验证真实身份的核心功能，请登录后使用。</p>
            <div className="flex gap-3">
              <button onClick={() => setVideoModal(false)} className="flex-1 py-2.5 border border-gray-200 rounded-full text-sm text-gray-600 cursor-pointer whitespace-nowrap">取消</button>
              <button className="flex-1 py-2.5 bg-red-600 text-white rounded-full text-sm font-medium cursor-pointer whitespace-nowrap">立即登录</button>
            </div>
          </div>
        </div>
      )}

      {/* Message Modal */}
      {messageModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setMessageModal(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            {messageSent ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-3">
                  <i className="ri-check-line text-green-500 text-2xl"></i>
                </div>
                <h3 className="font-bold text-gray-800 mb-1">消息已发送！</h3>
                <p className="text-gray-500 text-sm">登录后即可查看回复</p>
                <button onClick={() => { setMessageModal(false); setMessageSent(false); }} className="mt-4 text-red-500 text-sm cursor-pointer">关闭</button>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-bold text-gray-800 mb-4">发消息给 {member.name}</h3>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value.slice(0, 200))}
                  placeholder="写下您想说的话..."
                  rows={4}
                  maxLength={200}
                  className="w-full border border-gray-200 rounded-xl p-3 text-sm resize-none focus:outline-none focus:border-red-400 mb-2"
                />
                <div className="text-xs text-gray-400 text-right mb-4">{message.length}/200</div>
                <div className="flex gap-3">
                  <button onClick={() => setMessageModal(false)} className="flex-1 py-2.5 border border-gray-200 rounded-full text-sm text-gray-600 cursor-pointer whitespace-nowrap">取消</button>
                  <button onClick={() => setMessageSent(true)} className="flex-1 py-2.5 bg-red-600 text-white rounded-full text-sm font-medium cursor-pointer whitespace-nowrap">发送</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
